import type { SettlementView, WitnessSource } from "../world/types";

/*
 * The session's URL parameters: which town to start in, and whether the
 * development surfaces are wanted.
 *
 * Phase 4 decision 7 — **session shape**. The link a playtester is sent is a URL
 * and nothing else, so everything that varies between "the owner checking a
 * seed" and "somebody's first five minutes" has to live in the query string.
 * Three of those live here; `?seed`, `?bootstrap`, `?fixture` and `?book` are
 * older or newer and live where they are read (`world/wasm.ts`,
 * `world/fixture.ts`, `witness/saga.ts`).
 *
 * The rule this file exists to hold is the one devlog #10's postscript found the
 * hard way: **a dev-only affordance must not greet a playtester.** The trace
 * view is the chronicler's sentences rather than anybody's, and a self-check
 * verdict written across the HUD is a bug report a player did not ask for — so
 * both are behind `?dev=1` now, on top of the `import.meta.env.DEV` gate that
 * already keeps them out of a production bundle entirely.
 *
 * Nothing here reads `window`: every function takes the search string, so the
 * self-checks can ask the awkward questions without a browser to set up
 * (`dev/selfcheck.ts`, the same shape `fixtureRequested` is written in).
 */

/** `?dev=1` — the trace view and the self-checks. */
const DEV_PARAM = "dev";

/** `?debug=1` — the debug window open at boot (`dev/debugpanel.ts`). */
const DEBUG_PARAM = "debug";

/** `?home=Goldenhold`, `?home=3` — which settlement the session starts in. */
const HOME_PARAM = "home";

/** `?decay=730` — the salience-decay knob, as a half-life in days. */
const DECAY_PARAM = "decay";

/**
 * Returns whether the query string asks for the development surfaces.
 *
 * Any value except an explicit off counts as on, which is `fixtureRequested`'s
 * rule restated rather than shared: `world/fixture.ts`
 * is imported behind `import.meta.env.DEV` so that the fake data never reaches a
 * production bundle, and importing a function out of it here would drag the
 * whole module back in. Six lines duplicated against that is the right trade,
 * and this comment is the reason it is not a mistake.
 */
export function devRequested(search: string): boolean {
  return flagRequested(search, DEV_PARAM);
}

/**
 * Returns whether the session wants the debug window **already open** at boot.
 *
 * **This flag stopped being a gate at chunk D2**, on the owner's call: *"let's
 * keep the debug window always accessible with F3, I don't like having to open
 * a specific URL, and it's fine to have it while we develop."* The window is
 * built in every session now — production builds included — and `` ` `` or `F3`
 * toggles it wherever the client runs. What is left of `?debug=1` is the one
 * thing a URL is still better at than a key: **opening it before the first
 * frame**, which is what a link handed to somebody who is about to reproduce a
 * defect wants to say.
 *
 * The window was already gated on the link *alone* rather than also on
 * `import.meta.env.DEV`, for `?decay=`'s reason stated one paragraph up in this
 * file's own history — the session that gets judged is the deployed preview,
 * and `import.meta.env.DEV` is not true in one. The ruling above finishes that
 * argument rather than reversing it: a window the owner has to edit a URL to
 * reach is a window that is not at hand.
 *
 * **`?dev=1` is deliberately not folded in here, and it used to be.** While
 * this answered *"build the window"* a dev session wanted one; now it answers
 * *"open the window"*, and a dev session that opened it at boot would put an
 * overlay across every still `scripts/shots.mjs` takes. Somebody who wants both
 * asks for both.
 */
export function debugAtBoot(search: string): boolean {
  return flagRequested(search, DEBUG_PARAM);
}

/**
 * Returns what `?home=` asked for, trimmed, or `null` when it asked for nothing.
 *
 * The raw string rather than a resolved settlement, because resolving needs the
 * simulation and parsing does not — and because the two failures are different:
 * a parameter that names nowhere is worth saying out loud
 * ({@link resolveHome} answers `null` and `main.ts` puts it on the HUD), and a
 * parameter that is absent is the ordinary case.
 */
export function homeRequested(search: string): string | null {
  const raw = new URLSearchParams(search).get(HOME_PARAM);

  if (raw === null || raw.trim() === "") {
    return null;
  }

  return raw.trim();
}

/**
 * Returns the settlement `?home=` names, or `null` when nothing matches.
 *
 * Matched by **name first, then by id**, and both are the simulation's own: a
 * name is what a scout report or a legends dump prints and an id is what a
 * headless assert pins (`crates/sim-cli`'s scout). Case-insensitive, because a
 * link typed by hand is a link with a lowercase town in it.
 *
 * The client spawning the player somewhere is not the simulation learning
 * anything: **the player is not a sim entity** (Phase 3 decision 10), so a spawn
 * choice is presentation exactly like the crowd's anchors are, and the world
 * that gets built is the same world either way. What it changes is which
 * settlement's roster, market and roads the session opens on, which devlog #10
 * estimated roughly triples the odds of a session starting somewhere the war can
 * be seen from.
 */
export function resolveHome(request: string, source: WitnessSource): SettlementView | null {
  const wanted = request.toLowerCase();
  const settlements = source.settlements();
  const named = settlements.find((entry) => entry.name.toLowerCase() === wanted);

  if (named !== undefined) {
    return named;
  }

  const id = Number(request);

  if (!Number.isInteger(id)) {
    return null;
  }

  return settlements.find((entry) => entry.settlement === id) ?? null;
}

/**
 * Returns the salience-decay half-life `?decay=` asked for **in days**, or
 * `null` for off.
 *
 * Phase 6 decision 7 and O7: decay ships behind a knob, default off, judged in
 * the owner's playtest before it becomes the default — and the knob is a URL
 * parameter rather than a build flag, because the session that gets judged is
 * the *deployed* preview and `import.meta.env.DEV` is not true in one.
 *
 * **Days, not years, and not a bare "on".** The simulation's own default is a
 * half-life of two years, and this client has no way to spell that: it does not
 * know how long a year is and is not going to learn (`world/types.ts`'s
 * `WorldDateView` — comparing two dates is the whole of the arithmetic it is
 * allowed). Naming a year count here would be inventing the calendar's number in
 * JavaScript, so the link names days and the value it names is put on the HUD
 * when it is applied (`witness/saga.ts`'s `applySalienceDecay`) — which is how
 * somebody reading a playtest link knows what they asked for.
 *
 * Hostile input is *off* rather than a guess, which is the shape of the rest of
 * this file: a link that asked for nonsense gets the shipped behaviour, and the
 * shipped behaviour is the knob turned off. So `?decay=0`, `?decay=off`,
 * `?decay=-3`, `?decay=banana` and an absent parameter are all the same session.
 * Fractions are floored: a day is the smallest thing this client counts.
 */
export function decayRequested(search: string): number | null {
  const raw = new URLSearchParams(search).get(DECAY_PARAM);

  if (raw === null) {
    return null;
  }

  const days = Number(raw.trim());

  if (!Number.isFinite(days) || days < 1) {
    return null;
  }

  return Math.floor(days);
}

/** The shared spelling of an on/off URL flag. Absent is off; an explicit off is off. */
function flagRequested(search: string, name: string): boolean {
  const raw = new URLSearchParams(search).get(name);

  if (raw === null) {
    return false;
  }

  const value = raw.trim().toLowerCase();

  return value !== "0" && value !== "false" && value !== "off";
}
